<script lang="ts" setup>
import { computed, nextTick, onBeforeMount, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"

import { formatDate, formatEvolution, formatNumber } from "@core/helpers"
import { isGoAsyncByConfig } from "@modules/energy-manager/helpers"
import { useToast } from "primevue/usetoast"
import moment from "moment"
import { VARIABLES } from "@modules/energy-manager/constants"
import { Freq } from "@types"
import { ServiceError } from "@core/exceptions"

const emConsoCompareStore = useEmConsoCompareStore()

const { t } = useI18n({
    messages: {
        fr: {
            title: "Suivi global",
            submit: "Lancer",
            compare: {
                button: "Par rapport à",
            },
            export_data: "Exporter les données",
            error: {
                title: ({
                    named,
                }: {
                    named: (key: string) => string | string[]
                }) => {
                    const code = named("code") as string
                    const mappingCode = {
                        "aucune-donnee": `Impossible de lancer l'analyse`,
                    } as Record<string, string>

                    return mappingCode?.[code] ?? "Erreur"
                },
                detail: ({
                    named,
                }: {
                    named: (key: string) => string | string[]
                }) => {
                    const code = named("code") as string
                    const period = named("period") as string[]
                    const variable = named("variable") as string
                    const reason = named("reason") as string
                    const mappingCode = {
                        "aucune-donnee": `Aucune donnée ${variable} trouvée sur la période entre le ${period[0]} et le ${period[1]}. Éssayer sur une autre période ou vérifier ${reason}.`,
                    } as Record<string, string>

                    return (
                        mappingCode?.[code] ??
                        "Une erreur est survenue lors de la récupération des données"
                    )
                },
            },
        },
    },
})

// ============================================
// Gestion du formulaire
// ============================================

// Variable à comparer
const variable = ref<string>("")

// La fréquence de comparaison
const freq = ref<Freq>("daily")

// Période de comparaison
const period = ref<[Date | null, Date | null]>([new Date(), new Date()])
// Type de choix dans le sélecteur de période (ex: semaine précédente, mois précédent)
const choice = ref<string>("")

// Indique si l'utilisateur souhaite comparer
const compare = ref(false)

// Comparer avec une période précédente ou de l'année précédente
const compareWithPeriod = ref<string>("before")

const compareDates = ref<[Date | null, Date | null]>([new Date(), new Date()])

/**
 * Quand l'utilisateur clique sur lancer l'analyse
 */
function onClickSubmit() {
    // met à jour le store avec les valeurs
    emConsoCompareStore.compareFreq = freq.value
    emConsoCompareStore.comparePeriod = [...period.value]
    emConsoCompareStore.compareVariable = variable.value

    const periodsFormated = [
        [
            moment(period.value[0]).format("YYYY-MM-DD"),
            moment(period.value[1]).format("YYYY-MM-DD"),
        ],
    ]

    if (compare.value && compareDates.value) {
        periodsFormated.push(
            compareDates.value.map((d) => moment(d).format("YYYY-MM-DD"))
        )
        emConsoCompareStore.comparePeriodWith = [...compareDates.value]
    }

    // Enregistre les derniers paramètres dans un localStorage
    localStorage.setItem(
        "accueil-em-preferences",
        JSON.stringify({
            freq: freq.value,
            periods: emConsoCompareStore.comparePeriod,
            variable: variable.value,
            compare: compare.value,
            choice: choice.value,
        })
    )

    const async = emConsoCompareStore.getDisplayDataConfig
        ? isGoAsyncByConfig(
              emConsoCompareStore.getDisplayDataConfig,
              period.value,
              freq.value
          )
        : false

    // appel de l'api pour calculer le graphique de comparaison
    emConsoCompareStore.fetchCompareGraphByBuildingIds.fetch({
        buildingIds: emConsoCompareStore.compareListId,
        freq: freq.value,
        periods: periodsFormated.reverse(),
        variable: variable.value,
        unit: VARIABLES[variable.value.split(".")[0]].unit,
        format: VARIABLES[variable.value.split(".")[0]].format,
        ranking_order: VARIABLES[variable.value.split(".")[0]].ranking_order,
        async,
    })
}

/**
 * Valeurs de comparaison
 */
const getDashboardCompareValues = computed(() => {
    return [
        emConsoCompareStore.compareFreq,
        emConsoCompareStore.comparePeriod,
        emConsoCompareStore.compareVariable,
    ]
})

watch(
    () => getDashboardCompareValues.value,
    () => {
        freq.value = emConsoCompareStore.compareFreq
        period.value = [...emConsoCompareStore.comparePeriod]
        if (emConsoCompareStore.compareVariable)
            variable.value = emConsoCompareStore.compareVariable
    },
    { immediate: true }
)

// ============================================
// Gestion des indicateurs
// ============================================

/**
 * Liste des indicateurs de comparaison
 */
const getIndicators = computed(() => {
    if (!emConsoCompareStore.compareVariable) return []

    const [splitVariable] = (emConsoCompareStore.compareVariable ?? "").split(
        "."
    )

    const variableConfig = VARIABLES[splitVariable]

    const maxDigit = parseInt(variableConfig?.format?.match(/\d+/)?.[0] ?? "1")
    const ranking_order = variableConfig?.ranking_order

    return emConsoCompareStore.comparePeriodIndicators.map(
        (indicator, index) => {
            let evolution = NaN
            let diff = NaN
            if (
                index === 1 &&
                emConsoCompareStore.comparePeriodIndicators.length > 1
            ) {
                // calcul de l'évolution entre les deux indicateurs
                diff =
                    indicator.value -
                    emConsoCompareStore.comparePeriodIndicators[0].value
                evolution =
                    diff / emConsoCompareStore.comparePeriodIndicators[0].value

                if (!isFinite(evolution)) {
                    evolution = NaN
                }
            }

            return {
                name: `${variableConfig.name} du ${formatDate(
                    indicator.date_from,
                    "DD/MM/YYYY"
                )} au ${formatDate(indicator.date_to, "DD/MM/YYYY")}`,
                value: formatNumber(indicator.value, {
                    style: "decimal",
                    unit: variableConfig.unit,
                    maximumFractionDigits: maxDigit,
                }),
                icon: variableConfig.icon,
                iconColor: variableConfig.iconColor,
                evolution: evolution,
                evolution_formated: isNaN(evolution)
                    ? null
                    : formatEvolution(evolution, diff, {
                          maxDigit,
                          unit: variableConfig.unit,
                      }),
                ranking_order,
            }
        }
    )
})

// ============================================
// Gestion du graphique de comparaison
// ============================================
const toast = useToast()
const errorMessage = ref<{ title: string; content: string } | null>(null)
watch(
    () => emConsoCompareStore.fetchCompareGraphByBuildingIds.error,
    (error) => {
        if (error) {
            const serviceError = error as ServiceError
            const variableKey =
                emConsoCompareStore.snapshotCompareBody?.variable?.split(
                    "."
                )?.[0] ?? ""
            errorMessage.value = {
                title: t("error.title", {
                    code: serviceError?.code,
                }),
                content: t("error.detail", {
                    code: serviceError?.code,
                    period: [
                        moment(period.value[0]).format("DD/MM/YYYY"),
                        moment(period.value[1]).format("DD/MM/YYYY"),
                    ],
                    reason: t(`variables.${variableKey}.empty_data`),
                    variable: t(`variables.${variableKey}.sentence_2`),
                }),
            }
            // Affiche une notification d'erreur
            toast.add({
                severity: "error",
                summary: errorMessage.value.title,
                detail: errorMessage.value.content,
                life: 6000,
            })
        } else {
            errorMessage.value = null
        }
    }
)

const hasGraphVisible = computed(() => {
    return (
        emConsoCompareStore.compareGraphData &&
        !emConsoCompareStore.fetchCompareGraphByBuildingIds.isFetching &&
        !emConsoCompareStore.isTaskRunning
    )
})

/**
 * Vérifie si la route de la comparaison a remontée une erreur
 */
const hasErrorNoData = computed(() => {
    return !!emConsoCompareStore.fetchCompareGraphByBuildingIds.error
})

// ============================================
// Cycle de vie
// ============================================
onBeforeMount(() => {
    // Récupère les derniers paramètres enregistrés dans le localStorage
    const preferences = JSON.parse(
        localStorage.getItem("accueil-em-preferences") || "{}"
    )
    if (Object.values(preferences).length && preferences.freq !== "yearly") {
        period.value = preferences.periods.map((p: string) => new Date(p))
        variable.value = preferences.variable
        compare.value = preferences.compare
        freq.value = preferences.freq
        choice.value = preferences.choice ?? "last_7days"
    }

    nextTick(() => {
        if (emConsoCompareStore.compareListId.length) {
            // lance le calcul du graphique
            onClickSubmit()
        }
    })
})

function getEvolutionTextClass(evolution: number, rankingOrder?: string) {
    const positive = evolution > 0
    const desc = rankingOrder === "desc"
    if (positive) {
        return desc ? "text-red-500" : "text-teal-500"
    }
    return desc ? "text-teal-500" : "text-red-500"
}
</script>

<template>
    <BaseCard
        class="dashboard-card-compare flex flex-col gap-6 border p-6 [--p-card-body-padding:0] [--p-card-shadow:none]"
    >
        <template #header>
            <h1 class="font-bold text-primary-700 text-xl">
                {{ t("title") }}
            </h1>
            <!-- Header -->
            <div class="dashboard-card-compare__header">
                <!-- Variable -->
                <DashboardCardCompareVariable v-model:variable="variable" />
                <!-- Sélecteur de fréquence -->
                <DashboardCardCompareFreq
                    v-model:freq="freq"
                    :variable="variable"
                />
                <!-- Sélecteur de date -->
                <DashboardCardComparePeriod
                    v-model:period="period"
                    v-model:choice="choice"
                    :variable="variable"
                    :freq="freq"
                />

                <!-- Comparer avec-->
                <div
                    class="dashboard-card-compare__compare flex w-40 items-center gap-2 pb-2"
                >
                    <BaseToggleSwitch v-model="compare" input-id="compare" />
                    <label for="compare">{{ t("compare.button") }}</label>
                </div>
                <!-- Sélecteur de comparaison -->
                <DashboardCardComparePeriodCompare
                    v-model:compare="compareWithPeriod"
                    :period="period"
                    :choice="choice"
                    :freq="freq"
                    :disabled="!compare"
                    @update:dates="compareDates = $event"
                />
                <BaseButton
                    class="dashboard-card-compare__submit"
                    :label="t('submit')"
                    icon="pi pi-check"
                    icon-pos="right"
                    :disabled="emConsoCompareStore.isTaskRunning"
                    @click="onClickSubmit"
                    :loading="
                        emConsoCompareStore.fetchCompareGraphByBuildingIds
                            .isFetching
                    "
                ></BaseButton>
            </div>
        </template>
        <template #content>
            <!-- Tâche -->
            <DashboardCardCompareTask
                v-if="emConsoCompareStore.isTaskRunning"
            />
            <!-- Indicateurs -->
            <div
                v-if="!emConsoCompareStore.isTaskRunning"
                class="dashboard-card-compare__indicators grid items-center justify-center gap-4 pb-6 [grid-template-columns:repeat(var(--indicator-count),0.5fr)] max-lg:[grid-template-columns:1fr]"
                :style="{ '--indicator-count': String(getIndicators.length) }"
            >
                <BaseIndicatorItem
                    v-for="(indicator, index) in getIndicators"
                    :key="index"
                    :name="indicator.name"
                    :value="indicator.value"
                    :icon="indicator.icon"
                    :icon-color="indicator.iconColor"
                    :loading="
                        emConsoCompareStore.fetchCompareGraphByBuildingIds
                            .isFetching
                    "
                >
                    <template #value>
                        <span
                            v-if="indicator.evolution"
                            class="ml-2 text-xs"
                            :class="
                                getEvolutionTextClass(
                                    indicator.evolution,
                                    indicator.ranking_order
                                )
                            "
                            >{{ indicator.evolution_formated }}</span
                        >
                    </template>
                </BaseIndicatorItem>
            </div>
            <!-- Graph -->
            <div class="dashboard-card-compare__graph relative flex flex-col">
                <PlotlyChart
                    v-if="hasGraphVisible"
                    enable-export
                    :graph="JSON.parse(emConsoCompareStore.compareGraphData)"
                    @click:export="
                        emConsoCompareStore.fetchExportGraphData.fetch()
                    "
                />
                <BaseChartSkeleton
                    v-else-if="
                        !hasGraphVisible && !emConsoCompareStore.isTaskRunning
                    "
                    class="mt-2"
                ></BaseChartSkeleton>
                <BaseAppMessage
                    v-if="hasErrorNoData"
                    severity="error"
                    class="dashboard-card-compare__graph-message absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 md:max-w-fit w-full md:w-auto"
                >
                    <template #header>
                        {{ errorMessage?.title }}
                    </template>
                    {{ errorMessage?.content }}
                </BaseAppMessage>
            </div>
        </template>
    </BaseCard>
</template>

<style scoped>
@import "@styles/breakpoints.css";

.dashboard-card-compare__header {
    margin-top: 1rem;
    display: grid;
    align-items: flex-end;
    gap: 1rem;
    grid-template-areas: "variable freq period compare-switch compare submit";
    grid-template-columns: 13rem 11.1rem 11em 9.5rem 12rem auto;

    @media (--xlarge-viewport) {
        grid-template-areas:
            "variable variable freq freq"
            "period compare-switch compare submit";
        grid-template-columns: none;
    }

    @media (--large-viewport) {
        grid-template-areas:
            "variable variable freq"
            "period compare-switch compare"
            "submit submit submit";
        grid-template-columns: none;
    }

    @media (--medium-viewport) {
        grid-template-areas:
            "variable"
            "freq"
            "period"
            "compare-switch"
            "compare"
            "submit";
        grid-template-columns: none;
    }
}

.dashboard-card-compare__compare {
    grid-area: compare-switch;
}

.dashboard-card-compare__submit {
    grid-area: submit;
}

.dashboard-card-compare-freq {
    grid-area: freq;
}

.dashboard-card-compare-variable {
    grid-area: variable;
}

.dashboard-card-compare-period {
    grid-area: period;
}

.dashboard-card-compare-period-compare {
    grid-area: compare;
}
</style>
